import BaseUpload, {
    InitialFile,
    InitialPlaceholderFile,
    InitialTusFile,
    UploadType
} from "./base_upload";
import { deleteUpload } from "./tus_utils";

interface BaseUploadedFileParameters {
    name: string;
    size: number;
    type: UploadType;
    uploadIndex: number;
}

export abstract class BaseUploadedFile extends BaseUpload {
    size: number;

    constructor({ name, size, type, uploadIndex }: BaseUploadedFileParameters) {
        super({ name, status: "done", type, uploadIndex });

        this.size = size;
    }

    public async abort(): Promise<void> {
        return Promise.resolve();
    }

    public async delete(): Promise<void> {
        return Promise.resolve();
    }

    public getSize(): number | undefined {
        return this.size;
    }
}

class PlaceholderFile extends BaseUploadedFile {
    id: string;

    constructor(initialFile: InitialPlaceholderFile, uploadIndex: number) {
        super({
            name: initialFile.name,
            size: initialFile.size,
            type: "placeholder",
            uploadIndex
        });

        this.id = initialFile.id;
    }

    public getInitialFile(): InitialPlaceholderFile {
        return {
            id: this.id,
            name: this.name,
            size: this.size,
            type: "placeholder"
        };
    }
}

interface UploadedTusFileParameters {
    csrfToken: string;
    initialFile: InitialTusFile;
    uploadIndex: number;
    uploadUrl: string;
    keepOnServer?: boolean;
}

export class UploadedTusFile extends BaseUploadedFile {
    csrfToken: string;
    id: string;
    url: string;
    keepOnServer: boolean;

    constructor({
        csrfToken,
        initialFile,
        uploadIndex,
        uploadUrl,
        keepOnServer = false
    }: UploadedTusFileParameters) {
        super({
            name: initialFile.name,
            size: initialFile.size,
            type: "uploadedTus",
            uploadIndex
        });

        this.csrfToken = csrfToken;
        this.id = initialFile.id;
        this.url = `${uploadUrl}${initialFile.id}`;
        this.keepOnServer = keepOnServer;
    }

    public async delete(): Promise<void> {
        if (!this.keepOnServer) {
            await deleteUpload(this.url, this.csrfToken);
        }
        return Promise.resolve();
    }

    getInitialFile(): InitialTusFile {
        return {
            id: this.id,
            name: this.name,
            size: this.size,
            type: "tus",
            url: ""
        };
    }
}

interface UploadedFileParameters {
    csrfToken: string;
    initialFile: InitialFile;
    uploadIndex: number;
    uploadUrl: string;
    keepOnServer?: boolean;
}

export const createUploadedFile = ({
    csrfToken,
    initialFile,
    uploadIndex,
    uploadUrl,
    keepOnServer = false
}: UploadedFileParameters): BaseUploadedFile => {
    switch (initialFile.type) {
        case "placeholder":
            return new PlaceholderFile(initialFile, uploadIndex);

        case "tus":
            return new UploadedTusFile({
                csrfToken,
                initialFile,
                uploadUrl,
                uploadIndex,
                keepOnServer
            });
    }
};
